import React, { useState, useMemo, useCallback } from "react";
import {
  Tree,
  Input,
  Button,
  Table,
  Tag,
  Modal,
  List,
  Divider,
  message,
  Space,
  Alert,
  Tooltip,
  Spin,
} from "antd";
import type { DataNode, EventDataNode } from "antd/es/tree";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch, faCodeBranch, faListUl } from "@fortawesome/free-solid-svg-icons";
import {
  SchemaNode,
  SchemaBranchNode,
  SchemaEnumNode,
  SchemaUnionNode,
  SchemaTableNode,
  SchemaEnumEntry,
  DeviceType,
  fetchAndParseFile,
  buildFileSubtree,
} from "./FlatBufferSchemaTree";
import { validateEnumValueName, validateEnumAddition } from "./SchemaValidation";
import UnionEditor from "./UnionEditor";
import TableEditor, { SchemaTypeName } from "./TableEditor";

// ************************************************ */
// INTERFACES ************************************* */

export interface PendingAddition {
  enumNode: SchemaEnumNode;
  newValueName: string;
  newValueIndex: number;
}

export interface PendingUnionAddition {
  unionNode: SchemaUnionNode;
  newMemberName: string;
}

export interface PendingTableFieldAddition {
  tableNode: SchemaTableNode;
  newFieldName: string;
  newFieldType: string;
}

export type PendingChange =
  | { kind: "enum"; data: PendingAddition }
  | { kind: "union"; data: PendingUnionAddition }
  | { kind: "table"; data: PendingTableFieldAddition };

interface SchemaTreeEditorProps {
  deviceType: DeviceType | null;
  schemaTree: SchemaBranchNode;
  githubToken: string;
  onCreatePR?: (
    enumAdditions: PendingAddition[],
    unionAdditions: PendingUnionAddition[],
    tableAdditions: PendingTableFieldAddition[],
  ) => void;
}

// ************************************************ */
// HELPERS **************************************** */

function validateNewName(name: string, existingNames: string[]): string | null {
  const nameErrors = validateEnumValueName(name);
  if (nameErrors.length > 0) {
    return nameErrors[0].message;
  }

  const addErrors = validateEnumAddition(existingNames, [name]);
  const dupeError = addErrors.find((e) => e.field === "duplicate");
  if (dupeError) {
    return dupeError.message;
  }

  return null;
}

/** Generate a unique key for a tree node based on its path */
function nodeKey(parentKey: string, label: string): string {
  return parentKey ? `${parentKey}/${label}` : label;
}

/** Convert SchemaNode tree to Ant Design DataNode tree */
function schemaToDataNodes(nodes: SchemaNode[], parentKey: string): DataNode[] {
  return nodes.map((node) => {
    const key = nodeKey(parentKey, node.label);

    switch (node.kind) {
      case "branch":
        return {
          key,
          title: node.label,
          icon: null,
          children: schemaToDataNodes(node.children, key),
          selectable: false,
        };

      case "file": {
        const fileNode: DataNode = {
          key,
          title: node.label,
          isLeaf: false,
          selectable: false,
        };
        if (node.loaded && node.children.length > 0) {
          fileNode.children = schemaToDataNodes(node.children, key);
        }
        return fileNode;
      }

      case "enum":
        return {
          key,
          title: (
            <span>
              {node.label}{" "}
              <Tag color="blue" style={{ fontSize: 10 }}>
                enum
              </Tag>
            </span>
          ),
          isLeaf: true,
          selectable: true,
        };

      case "table":
        return {
          key,
          title: (
            <span>
              {node.label}{" "}
              <Tag color="green" style={{ fontSize: 10 }}>
                table
              </Tag>
            </span>
          ),
          isLeaf: true,
          selectable: true,
        };

      case "struct":
        return {
          key,
          title: (
            <span>
              {node.label}{" "}
              <Tag color="orange" style={{ fontSize: 10 }}>
                struct
              </Tag>
            </span>
          ),
          isLeaf: false,
          selectable: false,
          children: node.fields.map((f, i) => ({
            key: `${key}/field-${i}`,
            title: f,
            isLeaf: true,
            selectable: false,
          })),
        };

      case "union":
        return {
          key,
          title: (
            <span>
              {node.label}{" "}
              <Tag color="purple" style={{ fontSize: 10 }}>
                union
              </Tag>
            </span>
          ),
          isLeaf: true,
          selectable: true,
        };

      default:
        return { key, title: "unknown", isLeaf: true };
    }
  });
}

/** Recursively find a selectable SchemaNode (enum, union, or table) by matching key path */
function findSelectableByKey(
  nodes: SchemaNode[],
  targetKey: string,
  parentKey: string,
): SchemaEnumNode | SchemaUnionNode | SchemaTableNode | null {
  return nodes.reduce<SchemaEnumNode | SchemaUnionNode | SchemaTableNode | null>((found, node) => {
    if (found) return found;
    const key = nodeKey(parentKey, node.label);
    if (
      key === targetKey &&
      (node.kind === "enum" || node.kind === "union" || node.kind === "table")
    ) {
      return node;
    }
    if ((node.kind === "branch" || node.kind === "file") && node.children.length > 0) {
      return findSelectableByKey(node.children, targetKey, key);
    }
    return null;
  }, null);
}

// ************************************************ */
// COMPONENT ************************************** */

function SchemaTreeEditor({
  deviceType,
  schemaTree: initialTree,
  githubToken,
  onCreatePR = undefined,
}: SchemaTreeEditorProps) {
  const [schemaTree, setSchemaTree] = useState<SchemaBranchNode>(initialTree);
  const [selectedNodeKey, setSelectedNodeKey] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [newValueName, setNewValueName] = useState("");
  const [pendingAdditions, setPendingAdditions] = useState<PendingAddition[]>([]);
  const [pendingUnionAdditions, setPendingUnionAdditions] = useState<PendingUnionAddition[]>([]);
  const [pendingTableAdditions, setPendingTableAdditions] = useState<PendingTableFieldAddition[]>(
    [],
  );
  const [showSummary, setShowSummary] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [loadingFiles, setLoadingFiles] = useState<Set<string>>(new Set());

  // ************************************************ */
  // COMPUTED VALUES ******************************** */

  const treeData = useMemo(
    () => schemaToDataNodes(schemaTree.children, schemaTree.label),
    [schemaTree],
  );

  const selectedNode = useMemo(() => {
    if (!selectedNodeKey) return null;
    return findSelectableByKey(schemaTree.children, selectedNodeKey, schemaTree.label);
  }, [selectedNodeKey, schemaTree]);

  const selectedEnum = selectedNode?.kind === "enum" ? selectedNode : null;
  const selectedUnion = selectedNode?.kind === "union" ? selectedNode : null;
  const selectedTable = selectedNode?.kind === "table" ? selectedNode : null;

  const displayValues: SchemaEnumEntry[] = useMemo(() => {
    if (!selectedEnum) return [];
    const base = [...selectedEnum.values];
    const additions = pendingAdditions
      .filter(
        (a) =>
          a.enumNode.fbsEnumName === selectedEnum.fbsEnumName &&
          a.enumNode.fbsNamespace === selectedEnum.fbsNamespace,
      )
      .map((a) => ({ name: a.newValueName, index: a.newValueIndex }));
    return [...base, ...additions];
  }, [selectedEnum, pendingAdditions]);

  const filteredValues = useMemo(() => {
    if (!searchTerm) return displayValues;
    const lower = searchTerm.toLowerCase();
    return displayValues.filter((v) => v.name.toLowerCase().includes(lower));
  }, [displayValues, searchTerm]);

  const pendingNames = useMemo(() => {
    if (!selectedEnum) return new Set<string>();
    return new Set(
      pendingAdditions
        .filter(
          (a) =>
            a.enumNode.fbsEnumName === selectedEnum.fbsEnumName &&
            a.enumNode.fbsNamespace === selectedEnum.fbsNamespace,
        )
        .map((a) => a.newValueName),
    );
  }, [selectedEnum, pendingAdditions]);

  /** Collect all user-defined type names from loaded schema files for the TableEditor type picker */
  const schemaTypes = useMemo(() => {
    const types: SchemaTypeName[] = [];
    function walk(nodes: SchemaNode[]) {
      nodes.forEach((node) => {
        if (node.kind === "enum") {
          types.push({
            name: node.fbsEnumName,
            kind: "enum",
            namespace: node.fbsNamespace,
          });
        } else if (node.kind === "table") {
          types.push({
            name: node.fbsTableName,
            kind: "table",
            namespace: node.fbsNamespace,
          });
        } else if (node.kind === "union") {
          types.push({
            name: node.fbsUnionName,
            kind: "union",
            namespace: node.fbsNamespace,
          });
        } else if (node.kind === "struct") {
          types.push({
            name: node.label.replace(/^struct /, "").replace(/ \(.*/, ""),
            kind: "struct",
            namespace: "",
          });
        } else if (node.kind === "branch" || node.kind === "file") {
          walk(node.children);
        }
      });
    }
    walk(schemaTree.children);
    return types;
  }, [schemaTree]);

  // ************************************************ */
  // LAZY LOAD ************************************** */

  const handleLoadData = useCallback(
    async (treeNode: EventDataNode<DataNode>) => {
      const key = String(treeNode.key);

      // Find the file node in our schema tree
      const fileNode = (() => {
        // Walk the tree to find a file node matching this key
        function search(nodes: SchemaNode[], parentKey: string): SchemaNode | null {
          return nodes.reduce<SchemaNode | null>((found, node) => {
            if (found) return found;
            const nk = nodeKey(parentKey, node.label);
            if (nk === key && node.kind === "file") return node;
            if (node.kind === "branch") {
              return search(node.children, nk);
            }
            return null;
          }, null);
        }
        return search(schemaTree.children, schemaTree.label);
      })();

      if (!fileNode || fileNode.kind !== "file" || fileNode.loaded) return;

      const { filePath } = fileNode;
      setLoadingFiles((prev) => new Set(prev).add(filePath));

      try {
        const parsed = await fetchAndParseFile(filePath, githubToken);
        const subtree = buildFileSubtree(parsed, filePath);

        // Update the tree immutably — only clone nodes on the path to the target
        setSchemaTree((prev) => {
          function updateTree(nodes: SchemaNode[]): SchemaNode[] {
            let changed = false;
            const result = nodes.map((node) => {
              if (node.kind === "file" && node.filePath === filePath) {
                changed = true;
                return { ...node, loaded: true, children: subtree };
              }
              if (node.kind === "branch") {
                const updatedChildren = updateTree(node.children);
                if (updatedChildren === node.children) return node;
                changed = true;
                return { ...node, children: updatedChildren };
              }
              return node;
            });
            return changed ? result : nodes;
          }
          const updatedChildren = updateTree(prev.children);
          if (updatedChildren === prev.children) return prev;
          return { ...prev, children: updatedChildren };
        });
      } catch (err: any) {
        message.error(`Failed to parse ${filePath}: ${err.message || "Unknown error"}`);
      } finally {
        setLoadingFiles((prev) => {
          const next = new Set(prev);
          next.delete(filePath);
          return next;
        });
      }
    },
    [schemaTree, githubToken],
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const handleSelect = useCallback((selectedKeys: React.Key[]) => {
    if (selectedKeys.length === 0) {
      setSelectedNodeKey(null);
      return;
    }
    const key = String(selectedKeys[0]);
    setSelectedNodeKey(key);
    setSearchTerm("");
  }, []);

  const handleAddValue = useCallback(() => {
    if (!selectedEnum || !newValueName.trim()) return;

    const trimmed = newValueName.trim();
    const allNames = displayValues.map((v) => v.name);
    const error = validateNewName(trimmed, allNames);

    if (error) {
      message.error(error);
      return;
    }

    const nextIndex = displayValues.length;
    setPendingAdditions((prev) => [
      ...prev,
      {
        enumNode: selectedEnum,
        newValueName: trimmed,
        newValueIndex: nextIndex,
      },
    ]);
    setNewValueName("");
    message.success(`Added "${trimmed}" (will be saved when you create PR)`);
  }, [selectedEnum, newValueName, displayValues]);

  const handleBulkAdd = useCallback(() => {
    if (!selectedEnum || !bulkText.trim()) return;

    const lines = bulkText
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) {
      message.warning("No names found. Enter one name per line.");
      return;
    }

    const allNames = displayValues.map((v) => v.name);
    const errors: string[] = [];
    const validNames: string[] = [];

    lines.forEach((name) => {
      const error = validateNewName(name, [...allNames, ...validNames]);
      if (error) {
        errors.push(`${name}: ${error}`);
      } else {
        validNames.push(name);
      }
    });

    if (errors.length > 0) {
      message.error(`Validation failed for ${errors.length} name(s): ${errors[0]}`);
      return;
    }

    const baseIndex = displayValues.length;
    const newAdditions = validNames.map((name, i) => ({
      enumNode: selectedEnum,
      newValueName: name,
      newValueIndex: baseIndex + i,
    }));

    setPendingAdditions((prev) => [...prev, ...newAdditions]);
    setBulkText("");
    setBulkMode(false);
    message.success(`Added ${validNames.length} value(s)`);
  }, [selectedEnum, bulkText, displayValues]);

  const handleRemovePending = useCallback((index: number) => {
    setPendingAdditions((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const totalPendingCount =
    pendingAdditions.length + pendingUnionAdditions.length + pendingTableAdditions.length;

  const handleAddUnionMember = useCallback(
    (memberName: string) => {
      if (!selectedUnion) return;
      setPendingUnionAdditions((prev) => [
        ...prev,
        { unionNode: selectedUnion, newMemberName: memberName },
      ]);
    },
    [selectedUnion],
  );

  const handleAddTableField = useCallback(
    (fieldName: string, fieldType: string) => {
      if (!selectedTable) return;
      setPendingTableAdditions((prev) => [
        ...prev,
        {
          tableNode: selectedTable,
          newFieldName: fieldName,
          newFieldType: fieldType,
        },
      ]);
    },
    [selectedTable],
  );

  const handleCreatePR = useCallback(() => {
    if (totalPendingCount === 0) {
      message.warning("No changes to submit");
      return;
    }
    setShowSummary(true);
  }, [totalPendingCount]);

  const handleConfirmPR = useCallback(() => {
    setShowSummary(false);
    if (onCreatePR) {
      onCreatePR(pendingAdditions, pendingUnionAdditions, pendingTableAdditions);
    }
  }, [onCreatePR, pendingAdditions, pendingUnionAdditions, pendingTableAdditions]);

  // ************************************************ */
  // TABLE COLUMNS ********************************* */
  const columns = useMemo(
    () => [
      {
        title: "#",
        dataIndex: "index",
        key: "index",
        width: 60,
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (name: string) => {
          if (pendingNames.has(name)) {
            return (
              <span>
                {name} <Tag color="green">NEW</Tag>
              </span>
            );
          }
          return name;
        },
      },
    ],
    [pendingNames],
  );

  // ************************************************ */
  // GROUP PENDING BY FILE ************************** */
  const pendingByFile = useMemo(() => {
    const grouped: Record<string, PendingAddition[]> = {};
    pendingAdditions.forEach((a) => {
      const key = `${a.enumNode.fbsFile} :: ${a.enumNode.fbsEnumName}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(a);
    });
    return grouped;
  }, [pendingAdditions]);

  // ************************************************ */
  // RENDER ***************************************** */
  return (
    <div>
      <h4>FlatBuffer Schema Editor</h4>
      <p style={{ opacity: 0.65 }}>
        Browse all FlatBuffer schemas discovered from the repository. Expand directories and files
        to explore enums, tables, unions, and structs. Select an enum, union, or table to add new
        values, members, or fields.
        {deviceType && (
          <span>
            {" "}
            Filtered to <Tag color="blue">{deviceType}</Tag> messages.
          </span>
        )}
      </p>

      <div style={{ display: "flex", gap: 16 }}>
        {/* Schema Tree */}
        <div
          style={{
            width: 420,
            maxHeight: 600,
            overflow: "auto",
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            padding: 8,
          }}
        >
          {loadingFiles.size > 0 && (
            <div style={{ marginBottom: 8 }}>
              <Spin size="small" /> Loading file...
            </div>
          )}
          <Tree
            treeData={treeData}
            loadData={handleLoadData}
            onSelect={handleSelect}
            showLine
            defaultExpandedKeys={[]}
            blockNode
            height={560}
          />
        </div>

        {/* Detail Panel */}
        <div style={{ flex: 1 }}>
          {selectedUnion && (
            <UnionEditor
              unionNode={{
                name: selectedUnion.fbsUnionName,
                namespace: selectedUnion.fbsNamespace,
                members: [
                  ...selectedUnion.members,
                  ...pendingUnionAdditions
                    .filter(
                      (a) =>
                        a.unionNode.fbsUnionName === selectedUnion.fbsUnionName &&
                        a.unionNode.fbsNamespace === selectedUnion.fbsNamespace,
                    )
                    .map((a) => a.newMemberName),
                ],
              }}
              onAddMember={handleAddUnionMember}
            />
          )}

          {selectedTable && (
            <TableEditor
              tableNode={{
                name: selectedTable.fbsTableName,
                namespace: selectedTable.fbsNamespace,
                fields: [
                  ...selectedTable.fields,
                  ...pendingTableAdditions
                    .filter(
                      (a) =>
                        a.tableNode.fbsTableName === selectedTable.fbsTableName &&
                        a.tableNode.fbsNamespace === selectedTable.fbsNamespace,
                    )
                    .map((a) => `${a.newFieldName}: ${a.newFieldType}`),
                ],
              }}
              schemaTypes={schemaTypes}
              onAddField={handleAddTableField}
            />
          )}

          {selectedEnum ? (
            <div>
              <Divider orientation="left">
                {selectedEnum.fbsEnumName} ({selectedEnum.fbsNamespace}) -- {displayValues.length}{" "}
                values
              </Divider>

              <Input
                prefix={<FontAwesomeIcon icon={faSearch} />}
                placeholder="Search values..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                style={{ marginBottom: 12, maxWidth: 400 }}
                allowClear
              />

              <Table
                dataSource={filteredValues}
                columns={columns}
                rowKey="name"
                size="small"
                pagination={{ pageSize: 20, showSizeChanger: true }}
                scroll={{ y: 400 }}
              />

              {/* Add New Value — Single mode */}
              {!bulkMode && (
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <Input
                    placeholder="NewEnumName (PascalCase)"
                    value={newValueName}
                    onChange={(e) => setNewValueName(e.target.value)}
                    size="small"
                    style={{ maxWidth: 300 }}
                    onPressEnter={handleAddValue}
                  />
                  <Button
                    type="primary"
                    size="small"
                    icon={<FontAwesomeIcon icon={faPlus} style={{ marginRight: 4 }} />}
                    onClick={handleAddValue}
                    disabled={!newValueName.trim()}
                  >
                    Add Value
                  </Button>
                  <Tooltip title="Switch to bulk-add mode (paste multiple names)">
                    <Button
                      size="small"
                      icon={<FontAwesomeIcon icon={faListUl} style={{ marginRight: 4 }} />}
                      onClick={() => setBulkMode(true)}
                    >
                      Bulk Add
                    </Button>
                  </Tooltip>
                </div>
              )}

              {/* Add New Values — Bulk mode */}
              {bulkMode && (
                <div style={{ marginTop: 12 }}>
                  <Input.TextArea
                    placeholder={
                      "Paste one PascalCase name per line:\nMyNewMessage\nAnotherMessage\nThirdMessage"
                    }
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    rows={6}
                    style={{ maxWidth: 500, marginBottom: 8 }}
                  />
                  <Space>
                    <Button
                      type="primary"
                      size="small"
                      icon={<FontAwesomeIcon icon={faPlus} style={{ marginRight: 4 }} />}
                      onClick={handleBulkAdd}
                      disabled={!bulkText.trim()}
                    >
                      Add All
                    </Button>
                    <Button size="small" onClick={() => setBulkMode(false)}>
                      Cancel
                    </Button>
                  </Space>
                </div>
              )}

              <Alert
                message="New values are appended to the end of the enum. They will not be saved until you click 'Review & Create PR' below."
                type="info"
                showIcon
                style={{ marginTop: 12 }}
              />
            </div>
          ) : null}

          {!selectedEnum && !selectedUnion && !selectedTable && (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                color: "#999",
              }}
            >
              <p>Select an enum, union, or table from the tree to view and edit.</p>
              <p style={{ fontSize: 12 }}>
                Expand directories to see .fbs files. Expand files to see their enums, tables, and
                unions.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pending Changes & Create PR */}
      {totalPendingCount > 0 && (
        <div style={{ marginTop: 24 }}>
          <Divider orientation="left">Pending Changes ({totalPendingCount})</Divider>
          <List
            size="small"
            bordered
            dataSource={[
              ...pendingAdditions.map((a, i) => ({
                kind: "enum" as const,
                index: i,
                label: (
                  <span>
                    <Tag color="blue">{a.enumNode.fbsFile}</Tag>
                    <strong>{a.enumNode.fbsEnumName}</strong> :{" "}
                    <Tag color="green">{a.newValueName}</Tag>
                    (index {a.newValueIndex})
                  </span>
                ),
              })),
              ...pendingUnionAdditions.map((a, i) => ({
                kind: "union" as const,
                index: i,
                label: (
                  <span>
                    <Tag color="purple">{a.unionNode.fbsFile}</Tag>
                    <strong>{a.unionNode.fbsUnionName}</strong> :{" "}
                    <Tag color="purple">{a.newMemberName}</Tag>
                  </span>
                ),
              })),
              ...pendingTableAdditions.map((a, i) => ({
                kind: "table" as const,
                index: i,
                label: (
                  <span>
                    <Tag color="green">{a.tableNode.fbsFile}</Tag>
                    <strong>{a.tableNode.fbsTableName}</strong> :{" "}
                    <Tag color="cyan">
                      {a.newFieldName}: {a.newFieldType}
                    </Tag>
                  </span>
                ),
              })),
            ]}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    type="link"
                    danger
                    size="small"
                    onClick={() => {
                      if (item.kind === "enum") handleRemovePending(item.index);
                      else if (item.kind === "union")
                        setPendingUnionAdditions((prev) => prev.filter((_, i) => i !== item.index));
                      else
                        setPendingTableAdditions((prev) => prev.filter((_, i) => i !== item.index));
                    }}
                    key="remove"
                  >
                    Remove
                  </Button>,
                ]}
              >
                {item.label}
              </List.Item>
            )}
          />
          <div style={{ marginTop: 12, textAlign: "right" }}>
            <Button
              type="primary"
              icon={<FontAwesomeIcon icon={faCodeBranch} style={{ marginRight: 4 }} />}
              onClick={handleCreatePR}
            >
              Review & Create PR
            </Button>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      <Modal
        title="Review Schema Changes"
        open={showSummary}
        onOk={handleConfirmPR}
        onCancel={() => setShowSummary(false)}
        okText="Create Pull Request & Merge"
        width={700}
      >
        <p>
          The following changes will be committed to the <strong>flatbuffer_schemas</strong>{" "}
          repository:
        </p>
        {Object.entries(pendingByFile).map(([key, additions]) => (
          <div key={key} style={{ marginBottom: 16 }}>
            <h5>
              <code>{key}</code>
            </h5>
            <List
              size="small"
              dataSource={additions}
              renderItem={(a) => (
                <List.Item>
                  <Space>
                    <Tag color="green">+ {a.newValueName}</Tag>
                    <span style={{ opacity: 0.65 }}>(appended at index {a.newValueIndex})</span>
                  </Space>
                </List.Item>
              )}
            />
          </div>
        ))}
        <Alert
          message="This will create a branch, commit the changes, open a PR, and merge it to main."
          type="warning"
          showIcon
        />
      </Modal>
    </div>
  );
}

export default SchemaTreeEditor;
